import React from 'react';
import { cn } from '../../../utils/cn';

const AccountStatusSection = ({
  formData,
  onStatusChange,
  originalUser
}) => {
  const hasStatusChanged = formData?.status !== originalUser?.status;

  const statusOptions = [
    {
      value: 'active',
      label: 'Active',
      description: 'User can access the system and perform actions',
      color: 'text-green-600',
      bg: 'bg-green-50 border-green-200',
      dot: 'bg-green-500'
    },
    {
      value: 'inactive',
      label: 'Inactive',
      description: 'User access is suspended but account is preserved',
      color: 'text-red-600',
      bg: 'bg-red-50 border-red-200',
      dot: 'bg-red-500'
    }
  ];

  const currentStatusInfo = statusOptions?.find(option => option?.value === formData?.status) || statusOptions?.[0];

  return (
    <div className="bg-white rounded-lg border border-border shadow-sm p-6">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-foreground mb-2">
          Account Status Controls
        </h2>
        <p className="text-sm text-muted-foreground">
          Manage user account activation and access permissions
        </p>
      </div>
      <div className="space-y-6">
        {/* Current Status Display */}
        <div className={cn("rounded-lg p-4 border", currentStatusInfo?.bg)}>
          <div className="flex items-center space-x-3 mb-2">
            <div className={cn("w-3 h-3 rounded-full", currentStatusInfo?.dot)}></div>
            <h3 className={cn("font-medium", currentStatusInfo?.color)}>
              {currentStatusInfo?.label}
              {hasStatusChanged && (
                <span className="ml-2 text-xs text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full">
                  Modified
                </span>
              )}
            </h3>
          </div>
          <p className="text-sm text-muted-foreground">
            {currentStatusInfo?.description}
          </p>
        </div>

        {/* Status Toggle Options */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-foreground">Change Status</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {statusOptions?.map((option) => (
              <button
                key={option?.value}
                onClick={() => onStatusChange?.(option?.value)}
                className={cn(
                  "p-4 rounded-lg border text-left transition-colors",
                  formData?.status === option?.value
                    ? cn(option?.bg, "ring-2 ring-offset-2", option?.color?.replace('text-', 'ring-'))
                    : "border-border hover:border-muted bg-background hover:bg-muted/50"
                )}
              >
                <div className="flex items-center space-x-3 mb-2">
                  <div className={cn(
                    "w-2 h-2 rounded-full",
                    formData?.status === option?.value ? option?.dot : "bg-muted"
                  )}></div>
                  <span className={cn(
                    "font-medium",
                    formData?.status === option?.value ? option?.color : "text-foreground"
                  )}>
                    {option?.label}
                  </span>
                  {formData?.status === option?.value && (
                    <svg className={cn("w-4 h-4", option?.color)} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {option?.description}
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* Status Change Impact */}
        {hasStatusChanged && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <h4 className="font-medium text-yellow-800 mb-1">Status Change Impact</h4>
                <div className="text-sm text-yellow-700 space-y-1">
                  <p>Status will change from "{originalUser?.status}" to "{formData?.status}"</p>
                  {formData?.status === 'inactive' && originalUser?.status === 'active' && (
                    <ul className="list-disc list-inside space-y-1 mt-2">
                      <li>User will be immediately logged out of all sessions</li>
                      <li>User cannot access the system until reactivated</li>
                      <li>All scheduled tasks for this user will be paused</li>
                    </ul>
                  )}
                  {formData?.status === 'active' && originalUser?.status === 'inactive' && (
                    <ul className="list-disc list-inside space-y-1 mt-2">
                      <li>User will regain access to the system</li>
                      <li>Previous permissions and settings will be restored</li>
                      <li>User will receive a notification about account reactivation</li>
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountStatusSection;